import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Files, FunnelSimple, DownloadSimple, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusPill = ({ status }) => {
  const config = {
    draft: { label: 'Brouillon', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-300' },
    sent: { label: 'Envoyé', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
    accepted: { label: 'Accepté', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    signed: { label: 'Signé', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    rejected: { label: 'Refusé', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
  };
  const c = config[status] || config.draft;
  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>;
};

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/quotes`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur export');
    }
  };

  const filteredQuotes = filter === 'all' ? quotes : quotes.filter(q => q.status === filter);
  const filters = [
    { key: 'all', label: 'Tous' },
    { key: 'draft', label: 'Brouillons' },
    { key: 'sent', label: 'Envoyés' },
    { key: 'accepted', label: 'Acceptés' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Files size={20} className="text-blue-600" weight="duotone" />
          </div>
          <span className="text-lg font-semibold text-foreground">Devis</span>
          <span className="text-sm text-muted-foreground">({quotes.length})</span>
        </div>
        
        {quotes.length > 0 && (
          <button 
            onClick={handleExportCSV}
            className="w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
          >
            <DownloadSimple size={20} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              filter === f.key
                ? 'bg-black dark:bg-white text-white dark:text-black'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Quotes List */}
      <div className="space-y-3">
        {filteredQuotes.length > 0 ? (
          filteredQuotes.map((quote, index) => (
            <motion.div
              key={quote.quote_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/quotes/${quote.quote_id}`)}
              className="premium-card premium-card-hover p-4 sm:p-5"
            >
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-foreground">{quote.quote_number}</span>
                    <StatusPill status={quote.status} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{quote.client_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground">{quote.total_ttc?.toFixed(0)} €</p>
                  <p className="text-xs text-muted-foreground">TTC</p>
                </div>
                <ArrowRight size={18} className="text-muted-foreground flex-shrink-0" />
              </div>
            </motion.div>
          ))
        ) : (
          <div className="premium-card p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Files size={32} className="text-muted-foreground" weight="duotone" />
            </div>
            <p className="text-muted-foreground">Aucun devis</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
